/**
 * Light, dark, or whatever the phone is set to.
 *
 * Three states rather than two, because both explicit choices are ones a diver
 * actually wants: dark for planning in a bunk the night before, and *light*
 * pinned on regardless of the system, for a phone held up at a sunlit boat
 * rail where a dark screen is unreadable. "Auto" alone cannot express either.
 *
 * The palette lives entirely in `styles/index.css`; all this does is set the
 * attribute those rules key off, and keep the browser chrome in step.
 */

export const THEMES = ["auto", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = "dive-slate.theme.v1";

/** Address-bar and status-bar colour, matching --color-slate-board. */
const CHROME_COLOR: Record<"light" | "dark", string> = {
  light: "#c4cac2",
  dark: "#12181c",
};

export function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(raw as Theme) ? (raw as Theme) : "auto";
  } catch {
    // Private mode, or a locked-down webview. Following the system is a fine
    // place to land, and losing the preference must not take the app down.
    return "auto";
  }
}

export function saveTheme(theme: Theme): void {
  try {
    if (theme === "auto") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The theme still applies for this session; it just will not persist.
  }
}

/** What "auto" currently resolves to. */
export function systemTheme(): "light" | "dark" {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "auto" ? systemTheme() : theme;
}

/**
 * Put the choice on <html>, where the CSS can see it, and update the browser
 * chrome to match. Removing the attribute for "auto" is what lets the
 * `prefers-color-scheme` block in the stylesheet take back over.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "auto") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", CHROME_COLOR[resolveTheme(theme)]);
}

/**
 * Call `onChange` when the system flips while the app is on "auto" — a phone
 * crossing its sunset schedule with the app open, most often. Returns an
 * unsubscribe.
 */
export function onSystemThemeChange(onChange: () => void): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const query = matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
